import { isDefined } from "@clipboard-health/util-ts";
import { differenceInMilliseconds, parseISO } from "date-fns";

const MILLISECONDS_PER_HOUR = 1000 * 60 * 60;

interface IsBonusEligibleShiftProps {
  createdAt: string | undefined;
  minimumPostedHours: number;
}

export function isBonusEligibleShift(props: IsBonusEligibleShiftProps): boolean {
  const { createdAt, minimumPostedHours } = props;

  // Treat a misconfigured or zero threshold as "no posted-hours gate": every
  // shift passes the posted-hours check. Fallback is permissive because the
  // BFF already enforces geographic and licensing scope, so we don't risk
  // exposing out-of-scope shifts.
  if (!isValidMinimumPostedHours(minimumPostedHours) || minimumPostedHours === 0) {
    return true;
  }

  if (!isDefined(createdAt)) {
    return false;
  }

  const hoursSincePosted =
    differenceInMilliseconds(new Date(), parseISO(createdAt)) / MILLISECONDS_PER_HOUR;
  return hoursSincePosted >= minimumPostedHours;
}

// `minimumPostedHours` comes from a LaunchDarkly JSON variation, so guard
// against a misconfigured flag (string, negative, NaN, Infinity). Fractional
// hours are accepted (e.g. `0.5`) — the predicate converts a millisecond
// delta into hours, so sub-hour thresholds are honoured exactly. We avoid
// `differenceInHours` because its default rounding method (`trunc`) would
// drop fractional thresholds.
function isValidMinimumPostedHours(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
